'use client';

import { useState, useRef, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { ChevronDown } from 'lucide-react';
import { type Locale } from '@/i18n-config';
import { slugs } from '@/lib/slugs';

interface LanguageSwitcherProps {
    currentLang: Locale;
}

const languages: { code: Locale; label: string; name: string; flagClass: string }[] = [
    { code: 'tr', label: 'TR', name: 'Türkçe', flagClass: 'fi fi-tr' },
    { code: 'en', label: 'EN', name: 'English', flagClass: 'fi fi-gb' },
    { code: 'ar', label: 'AR', name: 'العربية', flagClass: 'fi fi-sa' },
];

// Manuel dil secimini hatirla; proxy anasayfada bu cerezi tarayici dilinden
// once kullanir (aksi halde TR secimi "/" -> "/en"e geri doner).
function rememberLocale(locale: Locale) {
    document.cookie = `NEXT_LOCALE=${locale}; path=/; max-age=31536000; samesite=lax`;
}

export default function LanguageSwitcher({ currentLang }: LanguageSwitcherProps) {
    const [isOpen, setIsOpen] = useState(false);
    const pathname = usePathname();
    const router = useRouter();
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getTargetPathFallback = (targetLang: Locale) => {
        if (!pathname) return '/';

        let pathWithoutLocale = pathname;
        if (currentLang !== 'tr' && pathname.startsWith(`/${currentLang}`)) {
            pathWithoutLocale = pathname.replace(`/${currentLang}`, '') || '/';
        }

        const segments = pathWithoutLocale.split('/').filter(Boolean);
        const localizedSegments = segments.map((segment) => {
            for (const variants of Object.values(slugs)) {
                if (variants[currentLang] === segment) {
                    return variants[targetLang];
                }
            }
            return segment;
        });

        const nextPath = `/${localizedSegments.join('/')}` || '/';
        return targetLang === 'tr' ? nextPath : `/${targetLang}${nextPath === '/' ? '' : nextPath}`;
    };

    const handleSwitchLanguage = async (targetLang: Locale) => {
        setIsOpen(false);
        if (targetLang === currentLang) return;

        rememberLocale(targetLang);
        const search = typeof window !== 'undefined' ? window.location.search : '';

        try {
            const params = new URLSearchParams({
                path: pathname || '/',
                from: currentLang,
                to: targetLang,
            });

            const res = await fetch(`/api/i18n/switch-path?${params.toString()}`);
            if (res.ok) {
                const data = await res.json();
                if (data?.path && typeof data.path === 'string') {
                    router.push(`${data.path}${search}`);
                    return;
                }
            }
        } catch {
            // Fall through to local fallback path if API resolver fails.
        }

        router.push(`${getTargetPathFallback(targetLang)}${search}`);
    };

    const currentLanguage = languages.find((lang) => lang.code === currentLang) || languages[0];

    return (
        <div className="relative z-[60]" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-1.5 py-1 text-[#0f4c81] transition-colors hover:text-[#00AEEF] focus:outline-none sm:gap-2"
            >
                <span className={`${currentLanguage.flagClass} rounded-sm shadow-sm`} />
                <span className="text-sm font-medium sm:text-base">{currentLanguage.label}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute top-full right-0 mt-2 w-44 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden py-1 animate-in fade-in zoom-in-95 duration-100 z-[70]">
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            type="button"
                            onClick={() => handleSwitchLanguage(lang.code)}
                            className={`flex w-full items-center px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors ${currentLang === lang.code ? 'bg-blue-50 text-[#0f4c81] font-medium' : 'text-gray-700'}`}
                        >
                            <span className={`mr-3 ${lang.flagClass} rounded-sm shadow-sm`} />
                            <span>{lang.name}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
